import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  CreditCard,
  CheckCircle,
  XCircle,
  PauseCircle,
  PlayCircle,
  AlertCircle,
  Plus,
  Minus,
  RefreshCw,
} from "lucide-react";
import {
  superAdminBillingService,
  type BillingTimelineEvent,
} from "@/services/superAdminBillingService";

type Props = {
  events: BillingTimelineEvent[];
  loading?: boolean;
};

export default function BillingTimeline({ events, loading }: Props) {
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => b.timestamp - a.timestamp);
  }, [events]);

  const getEventIcon = (type: string) => {
    const iconMap: Record<string, JSX.Element> = {
      "customer.subscription.created": <Plus className="h-4 w-4" />,
      "customer.subscription.updated": <RefreshCw className="h-4 w-4" />,
      "customer.subscription.deleted": <Minus className="h-4 w-4" />,
      "customer.subscription.paused": <PauseCircle className="h-4 w-4" />,
      "customer.subscription.resumed": <PlayCircle className="h-4 w-4" />,
      "customer.subscription.trial_will_end": (
        <AlertCircle className="h-4 w-4" />
      ),
      "invoice.payment_succeeded": (
        <CheckCircle className="h-4 w-4 text-green-600" />
      ),
      "invoice.payment_failed": <XCircle className="h-4 w-4 text-red-600" />,
      "payment_method.attached": <CreditCard className="h-4 w-4" />,
      "payment_method.detached": (
        <CreditCard className="h-4 w-4 text-gray-400" />
      ),
    };
    return iconMap[type] || <Calendar className="h-4 w-4" />;
  };

  const getEventColor = (type: string) => {
    if (type.includes("failed")) return "border-red-200 bg-red-50";
    if (type.includes("succeeded")) return "border-green-200 bg-green-50";
    if (type.includes("deleted") || type.includes("detached"))
      return "border-gray-200 bg-gray-50";
    if (type.includes("paused")) return "border-yellow-200 bg-yellow-50";
    return "border-blue-200 bg-blue-50";
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
            </div>
            <div className="flex-grow">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-3 w-48 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (sortedEvents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No billing events found
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
      <div className="space-y-6">
        {sortedEvents.map((event, index) => (
          <div key={`${event.timestamp}-${index}`} className="flex gap-4">
            <div className="flex-shrink-0 relative">
              <div
                className={`h-8 w-8 rounded-full border-2 flex items-center justify-center bg-white ${getEventColor(event.type)}`}
              >
                {getEventIcon(event.type)}
              </div>
            </div>
            <div className="flex-grow pb-6">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm text-gray-900">
                  {event.description}
                </span>
                <Badge variant="outline" className="text-xs">
                  {event.type}
                </Badge>
              </div>
              <div className="text-xs text-gray-500">
                {superAdminBillingService.formatDateTime(event.timestamp)}
              </div>
              {event.data?.id && (
                <div className="mt-1 text-xs text-gray-400 font-mono">
                  Event ID: {event.data.id}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
